import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import socketManager from '../../utils/socketManager';

export const usePredictionSocket = (userId) => {
  const { isAuthenticated, accessToken } = useSelector((state) => state.auth); // Added back accessToken
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    connecting: false,
    socketId: null,
    reconnectAttempts: 0,
    socketUrl: null,
    userType: null,
    sandboxUserId: null
  });
  
  // Ref to track the last connection parameters to prevent unnecessary reconnections
  const lastConnectionParams = useRef({ userId: null, accessToken: null });
  const timeoutRef = useRef(null);

  // Initialize socket connection when userId is provided and user is authenticated
  useEffect(() => {
    // Check if connection parameters have actually changed
    const hasParamsChanged = lastConnectionParams.current.userId !== userId || 
                           lastConnectionParams.current.accessToken !== accessToken;
    
    if (!hasParamsChanged && socketManager.socket && socketManager.socket.connected) {
      return;
    }
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Add a small delay to prevent rapid connect/disconnect cycles
    timeoutRef.current = setTimeout(() => {
      if (userId && isAuthenticated && accessToken) {
        lastConnectionParams.current = { userId, accessToken };
        socketManager.connect(accessToken, userId); // Pass accessToken for authentication
      } else {
        socketManager.disconnect();
      }
    }, 200);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [userId, isAuthenticated, accessToken]); // Added back accessToken dependency

  // Cleanup effect that only runs on component unmount
  useEffect(() => {
    return () => {
      // socketManager.disconnect(); // DISABLED - This will now use reference counting
    };
  }, []); // Empty dependency array ensures this only runs on mount/unmount

  // Set up socket event listeners
  useEffect(() => {
    const handleConnected = () => {
      setIsConnected(true);
      updateConnectionStatus();
    };

    const handleDisconnected = (reason) => {
      setIsConnected(false);
      updateConnectionStatus();
    };

    const handleError = (error) => {
      console.error('❌ Prediction socket error event received:', error);
      setIsConnected(false);
      updateConnectionStatus();
    };

    const handleAuthError = (error) => {
      console.error('❌ Prediction socket auth error event received:', error);
      setIsConnected(false);
      updateConnectionStatus();
    };

    const handleMaxReconnectAttempts = () => {
      setIsConnected(false);
      updateConnectionStatus();
    };

    // Register event listeners
    socketManager.on('connected', handleConnected);
    socketManager.on('disconnected', handleDisconnected);
    socketManager.on('error', handleError);
    socketManager.on('auth_error', handleAuthError);
    socketManager.on('max_reconnect_attempts', handleMaxReconnectAttempts);

    // Cleanup function
    return () => {
      socketManager.off('connected', handleConnected);
      socketManager.off('disconnected', handleDisconnected);
      socketManager.off('error', handleError);
      socketManager.off('auth_error', handleAuthError);
      socketManager.off('max_reconnect_attempts', handleMaxReconnectAttempts);
    };
  }, []);

  const updateConnectionStatus = useCallback(() => {
    const status = socketManager.getConnectionStatus();
    setConnectionStatus(status);
  }, []);

  // Periodic status updates
  useEffect(() => {
    const interval = setInterval(() => {
      updateConnectionStatus();
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [updateConnectionStatus]);

  const connect = useCallback(() => {
    if (accessToken && userId) {
      socketManager.connect(accessToken, userId); // Pass accessToken for authentication
    } else {
    }
  }, [accessToken, userId]); // Added back accessToken dependency

  const disconnect = useCallback(() => {
    socketManager.disconnect();
  }, []);

  const emit = useCallback((event, data) => {
    return socketManager.send(event, data);
  }, []);

  const on = useCallback((event, callback) => {
    socketManager.on(event, callback);
  }, []);

  const off = useCallback((event, callback) => {
    socketManager.off(event, callback);
  }, []);

  return {
    socket: socketManager.socket,
    connected: isConnected,
    connectionStatus,
    connect,
    disconnect,
    emit,
    on,
    off
  };
};